'use client';
import React, { useState } from 'react';

// Color palette inspired by the "Aura Wellness" image
const COLORS = {
  ACCENT_GOLD: '#c69c6d', // A muted, professional gold
  DARK_BG: '#101010',     // Deep black background
  LIGHT_TEXT: '#f0f0f0',  // Off-white text
};

// Utility function for robust image fallbacks
const handleImageError = (e, fallbackSrc) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = fallbackSrc;
  e.currentTarget.style.filter = 'grayscale(100%) brightness(50%)';
};

// Array of images structured for the 2x2 asymmetric layout
const galleryData = [
  {
    title: "The Pilates Studio",
    subtitle: "recision Engineering for the Human Body",
    default: "/ImageGallery/PilatesStudio1.webp",
    hover: "/ImageGallery/PilatesStudio2.webp",
    alt: 'An expansive view of the dedicated Pilates zone featuring multiple custom reformers. The warm wood flooring and integrated ceiling lighting create a focused, high-end atmosphere for core-centric training',
    layout: 'standard', // 1x1
  },
  {
    title: "Cardio Elite Zone",
    subtitle: "Metabolic Conditioning Without Compromise",
    default: "/ImageGallery/CardioElite1.webp",
    hover: "/ImageGallery/CardioElite2.webp",
    alt: 'High-performance treadmills and upright cycles positioned with a view. This area is designed for endurance and cardiovascular health using top-tier equipment that tracks your every metric',
    layout: 'tall', // 1x2 (Spans vertical)
  },
  {
    title: "Functional Training Floor",
    subtitle: "Mind & Body Balance",
    default: "/ImageGallery/FunctionalTraining1.webp",
    hover: "/ImageGallery/FunctionalTraining2.webp",
    alt: 'A wide-angle view of the functional area. The dual-texture flooring (rubber for heavy lifting and wood for movement) shows the versatility of the space, allowing for everything from heavy squats to battle rope intervals',
    layout: 'standard', // 1x1
  },
  {
    title: "The Art of Atmosphere",
    subtitle: "Inside Aura’s High-End Wellness Sanctuary",
    default: "/ImageGallery/ArtofAtmosphere1.webp",
    hover: "/ImageGallery/ArtofAtmosphere2.webp",
    alt: 'True transformation begins the moment you step into an environment that respects your ambition. At Aura, we have redefined the "gym" as a sanctuary of sophisticated design and peak athletic engineering. Our facility blends the warmth of natural wood finishes with the sharp precision of state-of-the-art equipment. Every element—from the custom hexagonal ceiling architecture to the biophilic green-wall dividers—is curated to transition the mind from the chaos of daily life into a state of deep, athletic focus',
    layout: 'large', // 2x1 (Spans horizontal)
  },
];

export default function AuraGallery() {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const fallbackImage = "https://placehold.co/600x400/374151/ffffff?text=CONTENT+MISSING";

  // Helper function to map layout type to Tailwind Grid classes
  // We use h-full for desktop so the grid-auto-rows dictates the height.
  // We use specific h-XX classes for mobile to ensure a nice stack.
  const getLayoutClasses = (layout) => {
    switch (layout) {
      case 'tall':
        // Mobile: h-80 (slightly taller), Desktop: Span 2 rows
        return 'h-80 md:h-full md:col-span-1 md:row-span-2';
      case 'large':
        // Mobile: h-64, Desktop: Span 2 cols
        return 'h-64 md:h-full md:col-span-2 md:row-span-1';
      case 'standard':
      default:
        // Mobile: h-64, Desktop: Span 1
        return 'h-64 md:h-full md:col-span-1 md:row-span-1';
    }
  };

  return (
    <section 
      className="font-sans relative w-full min-h-screen py-12 md:py-20"
    >
      {/* Background Gradient Container */}
      <div
        className="absolute inset-0 bg-gradient-to-br"
        style={{
          background: `linear-gradient(to bottom right, ${COLORS.DARK_BG}, #262626, #402313, ${COLORS.ACCENT_GOLD}, #d98d4a, #a56330, #262626, ${COLORS.DARK_BG})`,
          opacity: 0.95,
        }}
      ></div>

      {/* Content Container */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" style={{ color: COLORS.LIGHT_TEXT }}>
        
        {/* Header Section */}
        <div className="text-center mb-10 md:mb-16">
          <p className="text-sm md:text-base font-semibold uppercase tracking-widest mb-2" style={{ color: COLORS.ACCENT_GOLD }}>
            Exclusive Gallery
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight pb-2 drop-shadow-lg">
            Aura <span style={{ color: COLORS.ACCENT_GOLD }}>Wellness</span>
          </h1>
          <p className="mt-4 text-base md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Experience the harmony of mind and body through our curated selection of images.
          </p>
        </div>

        {/* Grid Layout Config:
          - Mobile: 1 Column, auto rows (height controlled by children)
          - Desktop: 2 Columns, rows fixed at 200px (md:auto-rows-[200px]). 
            This allows 'row-span-2' items to be exactly (200px * 2) + gap height.
        */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:auto-rows-[200px]">
          {galleryData.map((item, index) => {
            const isHovered = hoveredIndex === index;
            const layoutClasses = getLayoutClasses(item.layout);

            return (
              <div
                key={index}
                className={`relative rounded-lg overflow-hidden cursor-pointer group shadow-2xl transition-all duration-300 ease-in-out hover:scale-[1.01] hover:shadow-xl ${layoutClasses}`}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {/* Image Element */}
                <img
                  src={isHovered ? item.hover : item.default}
                  alt={item.alt}
                  className="w-full h-full object-cover transition-transform duration-700 transform group-hover:scale-[1.05] group-hover:opacity-90"
                  onError={(e) => handleImageError(e, fallbackImage)}
                />

                {/* Subtle Gold Tint Overlay on Hover */}
                <div
                  className={`absolute inset-0 transition-all duration-500 pointer-events-none
                  ${isHovered ? 'bg-[#c69c6d]/20' : 'bg-transparent'}`}
                ></div>

                {/* Dark Gradient Overlay for Text Readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent pointer-events-none"></div>

                {/* Overlay Content with Title/Text */}
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 pointer-events-none">
                  <h2
                    className={`text-xl md:text-3xl font-extrabold transition-transform duration-300 drop-shadow-lg ${isHovered ? 'translate-y-0' : 'translate-y-2'}`}
                    style={{ color: isHovered ? COLORS.ACCENT_GOLD : COLORS.LIGHT_TEXT }}
                  >
                    {item.title}
                  </h2>
                  <p
                    className={`text-[10px] md:text-xs font-medium uppercase tracking-widest mt-1 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-75'}`}
                    style={{ color: COLORS.ACCENT_GOLD }}
                  >
                    {item.subtitle}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}